export const STAFF_UNAUTHORIZED_EVENT = 'coop:staff-unauthorized';

const baseUrl = import.meta.env.VITE_API_BASE_URL;

if (!baseUrl) {
  throw new Error('VITE_API_BASE_URL is not configured');
}

type StaffApiError = Error & {
  status: number;
  body?: unknown;
  correlationId?: string | null;
};

const hasApiStatus = (err: unknown): err is { status: number } =>
  typeof err === 'object' &&
  err !== null &&
  'status' in err &&
  typeof (err as { status?: unknown }).status === 'number';

export type StaffPartyDetails = {
  language?: string | null;
  ageGroup?: string | null;
  comments?: string | null;
  [key: string]: unknown;
};

export type StaffNotificationPayload = {
  kind?: string;
  posTableId?: string;
  requestId?: string;
  [key: string]: unknown;
};

const createStaffApiError = (
  message: string,
  status: number,
  body?: unknown,
  correlationId?: string | null,
): StaffApiError => {
  const err = new Error(message) as StaffApiError;
  err.status = status;
  err.body = body;
  err.correlationId = correlationId;
  return err;
};

async function staffApi<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  token?: string,
  body?: unknown,
  extraHeaders?: HeadersInit,
): Promise<T> {
  const headers: HeadersInit = {
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(extraHeaders ?? {}),
  };

  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    cache: 'no-store',
  });

  if (!res.ok) {
    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
    if (res.status === 401 && token && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(STAFF_UNAUTHORIZED_EVENT));
    }
    throw createStaffApiError(
      `${method} ${path} failed with status ${res.status}`,
      res.status,
      parsed ?? undefined,
      res.headers.get('x-correlation-id'),
    );
  }

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return undefined as T;
}

export type StaffAuthResponse = {
  token: string;
  party: { id: string; name: string | null };
  merchant: {
    id: string;
    name: string | null;
    timezone?: string;
    uiPollingIntervalSec?: number | null;
    uiPollingLimitedIntervalSec?: number | null;
    addItemOnBooking?: boolean;
    reservationItemPosId?: string | null;
    waiterOccupancyReservationMinutes?: number | null;
  } | null;
  connectedAccountIds: string[];
  scheduleShiftId?: string;
  waiterCheckinBypassMode?: 'NONE' | 'GEOFENCE_ONLY' | 'TIME_AND_GEOFENCE';
};

export type StaffStationScope = 'WAITER' | 'BARTENDER' | 'KITCHEN' | 'CASHIER' | 'MANAGER' | 'STATION_ADMIN';

export type StaffStationCapability =
  | 'ORDER_TAKE'
  | 'ORDER_EDIT'
  | 'TABLE_CLAIM'
  | 'BAR_PRODUCTION'
  | 'KITCHEN_DISPLAY'
  | 'COUNTER_SALE'
  | 'CHECKOUT'
  | 'CASH_PAYMENT'
  | 'VOID'
  | 'REFUND'
  | 'STATION_SETTINGS';

export type StaffStationHeartbeatStatus = 'LIVE' | 'STALE' | 'OFFLINE' | 'NEVER_SEEN';

export type StaffStationBrowserInfo = {
  userAgent: string | null;
  platform: string | null;
  language: string | null;
  viewport: string | null;
  standalone: boolean | null;
};

export type StaffStationHeartbeatHealth = {
  status: StaffStationHeartbeatStatus;
  ageSec: number | null;
  staleAfterSec: number;
  offlineAfterSec: number;
};

export type StaffStation = {
  id: string;
  merchantId: string;
  merchantName: string | null;
  eventId: string | null;
  venueId: string | null;
  label: string;
  stationType: string;
  defaultLocale: string | null;
  timezone: string;
  allowedScopes: StaffStationScope[];
  allowedCapabilities: StaffStationCapability[];
  enabled: boolean;
  enrolledAt: string | null;
  lastSeenAt: string | null;
  appVersion: string | null;
  browserInfo: StaffStationBrowserInfo | null;
  health: StaffStationHeartbeatHealth;
};

export type StaffStationDeviceJobKind = 'POS_FISCAL' | 'PERIPHERAL';

export type StaffStationFailedJob = {
  id: string;
  jobKind: string;
  status: string;
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string | null;
  updatedAt: string;
};

export type StaffStationDeviceJobSummary = {
  kind: StaffStationDeviceJobKind;
  pendingCount: number;
  retryingCount: number;
  failedCount: number;
  lastFailedJob: StaffStationFailedJob | null;
};

export type StaffStationHealthResponse = {
  station: StaffStation;
  serverTime: string;
  heartbeat: StaffStationHeartbeatHealth;
  pos: {
    activeCount: number;
    totalCount: number;
    connections: Array<{
      id: string;
      provider: string;
      status: string;
      lastTestedAt: string | null;
      lastTestOk: boolean | null;
      lastError: string | null;
      updatedAt: string;
    }>;
  };
  devices: {
    fiscal: StaffStationDeviceJobSummary;
    peripheral: StaffStationDeviceJobSummary;
  };
  androidCompatibility: {
    minSupportedAndroidMajor: number;
    detectedAndroidMajor: number | null;
    supported: boolean | null;
  };
  deployment: {
    httpsRequired: boolean;
    cachePolicy: 'SHELL_AND_MENU_ONLY';
    offlineSourceOfTruth: false;
  };
};

export type StaffStationAuthResponse = {
  staff: { id: string; name: string | null };
  station: StaffStation;
  token: string;
  scopes: StaffStationScope[];
  capabilities: StaffStationCapability[];
  canReturnCooperativeOrders: boolean;
  defaultScope: StaffStationScope | null;
  expiresAt: string | null;
  activeShift: {
    attendanceId: string;
    roleId: string | null;
    roleName: string | null;
    roleCode: string | null;
    connectedAccountId: string;
    checkinAt: string;
  } | null;
};

export type StaffShift = {
  id: string;
  merchantId: string;
  date: string;
  start: string;
  end: string;
  isReserve?: boolean;
  roleName: string | null;
  roleCanUseWaiterApp?: boolean;
  checkinAllowedFrom: string;
  checkinRoundMinutes: number;
  checkoutRoundMinutes: number;
  attendance: {
    status: string;
    checkinAt: string;
    checkoutAt: string | null;
    checkoutReason: string | null;
    checkinOffsetMinutes: number | null;
    checkoutOffsetMinutes: number | null;
  } | null;
  status: 'scheduled' | 'active' | 'completed' | 'missed' | 'override';
  needsCheckoutReason: boolean;
};

export type StaffShiftsResponse = {
  shifts: StaffShift[];
  merchant?: {
    id: string;
    name: string | null;
    timezone: string;
    uiPollingIntervalSec?: number | null;
    uiPollingLimitedIntervalSec?: number | null;
    waiterOccupancyReservationMinutes?: number | null;
  };
};

export type StaffPayoutsResponse = {
  tips: {
    totals: Array<{ currency: string; paid: number; pending: number }>;
    periodStart: string | Date;
    periodEnd: string | Date;
  };
  accounts: Array<{
    id: string;
    name: string;
    provider: string | null;
    providerAccountId: string | null;
    stripeAccountId: string | null;
    status: 'PENDING' | 'ACTIVE' | 'RESTRICTED' | 'DISABLED';
    onboardingStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'INCOMPLETE' | 'COMPLETE';
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    requirementsDue: string[];
    defaultCurrency: string | null;
    dashboardUrl: string | null;
    fallbackUrl: string | null;
  }>;
  provider: string | null;
  dashboardUrl: string | null;
  fallbackUrl: string | null;
};

export type StaffConnectedAccountOnboardingResponse = {
  connectedAccountId: string;
  provider: 'STRIPE';
  onboarding: {
    connectedAccountId: string;
    stripeAccountId: string;
    clientSecret: string;
    onboardingStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'INCOMPLETE' | 'COMPLETE';
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted?: boolean;
    onboardingCompletedAt?: string | null;
    tosAcceptedAt?: string | null;
    requirements?: unknown;
  };
};

export type StaffServiceShiftParticipant = {
  participantId: string;
  label: string;
  partyId: string | null;
  connectedAccountId: string | null;
  merchantId: string | null;
  role: string | null;
  partyType: string | null;
};

export type StaffServiceShiftOpeningCashBalance = {
  participantId: string | null;
  label?: string | null;
  amountMinor: number;
};

export type StaffServiceShiftRecord = {
  id: string;
  merchantId: string;
  eventId: string;
  serviceDate: string;
  status: 'draft' | 'open' | 'closing' | 'closed';
  openedAt: string | null;
  closedAt: string | null;
  openedByPartyId: string | null;
  closedByPartyId: string | null;
  openingCashBalances: StaffServiceShiftOpeningCashBalance[];
  shiftCloseSnapshot: Record<string, unknown> | null;
  reconciliationState: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type StaffCurrentServiceShiftResponse = {
  scope: {
    merchantId: string;
    eventId: string | null;
    eventName: string | null;
    serviceDate: string;
    timezone: string;
    placeId: string | null;
    placeName: string | null;
  };
  serviceShift: StaffServiceShiftRecord | null;
  eventParticipants: StaffServiceShiftParticipant[];
  suggestedOpeningCashBalances: StaffServiceShiftOpeningCashBalance[];
  canOpen: boolean;
  code: string | null;
  message: string | null;
};

export async function loginWithPin(pin: string, merchant: string) {
  return staffApi<StaffAuthResponse>('POST', '/staff/auth/pin', undefined, { pin, merchant });
}

const stationBrowserInfo = () => {
  if (typeof window === 'undefined') return null;
  return {
    userAgent: window.navigator.userAgent,
    platform: window.navigator.platform,
    language: window.navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    standalone:
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true,
  };
};

export async function enrollStaffStation(enrollmentToken: string) {
  return staffApi<{ stationToken: string; station: StaffStation }>('POST', '/staff/stations/enroll', undefined, {
    enrollmentToken,
    appVersion: import.meta.env.VITE_APP_VERSION ?? null,
    browserInfo: stationBrowserInfo(),
  });
}

export async function fetchCurrentStaffStation(stationToken: string) {
  return staffApi<{ station: StaffStation }>('GET', '/staff/stations/current', stationToken);
}

export async function fetchCurrentStaffStationHealth(stationToken: string) {
  return staffApi<StaffStationHealthResponse>('GET', '/staff/stations/current/health', stationToken);
}

export async function retryStaffStationDeviceJob(
  stationToken: string,
  kind: StaffStationDeviceJobKind,
  jobId: string,
) {
  return staffApi<StaffStationHealthResponse>(
    'POST',
    `/staff/stations/current/health/jobs/${encodeURIComponent(kind)}/${encodeURIComponent(jobId)}/retry`,
    stationToken,
  );
}

export async function heartbeatStaffStation(stationToken: string) {
  return staffApi<{ station: StaffStation }>('PATCH', '/staff/stations/current/heartbeat', stationToken, {
    appVersion: import.meta.env.VITE_APP_VERSION ?? null,
    browserInfo: stationBrowserInfo(),
  });
}

export type KitchenStationDisplayJobsResponse = {
  serverTime: string;
  deviceEndpointId: string;
  displayJobs: Array<{
    id: string;
    status: string;
    kitchenProductionJobId: string | null;
    createdAt: string;
    devicePayload: unknown;
    productionJob: KitchenProductionJob | null;
  }>;
};

export async function fetchKitchenStationDisplayJobs(stationToken: string) {
  return staffApi<KitchenStationDisplayJobsResponse>(
    'GET',
    '/staff/stations/current/kitchen/production-jobs',
    stationToken,
  );
}

export async function markKitchenStationDisplayJobExecuted(stationToken: string, jobId: string) {
  return staffApi<{ ok: boolean }>(
    'POST',
    `/staff/stations/current/kitchen/display-jobs/${encodeURIComponent(jobId)}/executed`,
    stationToken,
  );
}

export async function updateKitchenStationProductionJobStatus(
  stationToken: string,
  jobId: string,
  toStatus: KitchenJobStatus,
) {
  return staffApi<{ ok: boolean; job: { id: string; status: KitchenJobStatus } }>(
    'POST',
    `/staff/stations/current/kitchen/production-jobs/${encodeURIComponent(jobId)}/status`,
    stationToken,
    { toStatus },
  );
}

export async function fetchKitchenStationStopListProducts(stationToken: string) {
  return staffApi<{ products: KitchenStopListProduct[] }>(
    'GET',
    '/staff/stations/current/kitchen/stop-list/products',
    stationToken,
  );
}

export async function setKitchenStationStopListProduct(stationToken: string, productId: string, body: {
  isStopped: boolean;
  balance?: number | null;
  reason?: string | null;
  expiresAt?: string | null;
}) {
  return staffApi<{ ok: boolean }>(
    'POST',
    `/staff/stations/current/kitchen/stop-list/products/${encodeURIComponent(productId)}`,
    stationToken,
    body,
  );
}

export async function authStaffStationPin(stationToken: string, pin: string) {
  return staffApi<StaffStationAuthResponse>('POST', '/staff/stations/current/auth/pin', stationToken, { pin });
}

export async function lockStaffStation(stationToken: string) {
  return staffApi<void>('POST', '/staff/stations/current/auth/lock', stationToken);
}

export async function consumeMagicLink(token: string) {
  return staffApi<StaffAuthResponse>('POST', '/staff/auth/magic-link/consume', undefined, { token });
}

export async function fetchStaffShifts(token: string, range?: { from?: string; to?: string }) {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
  const query = params.toString();
  return staffApi<StaffShiftsResponse>('GET', `/staff/shifts${query ? `?${query}` : ''}`, token);
}

export async function staffCheckIn(token: string, shiftId: string, coords?: { lat?: number; lng?: number }) {
  return staffApi<{ attendance: unknown }>('POST', `/staff/shifts/${shiftId}/check-in`, token, coords ?? {});
}

export async function staffCheckOut(
  token: string,
  shiftId: string,
  reason?: string | null,
  coords?: { lat?: number; lng?: number },
) {
  return staffApi<{ attendance: unknown }>('POST', `/staff/shifts/${shiftId}/check-out`, token, {
    reason: reason ?? null,
    ...(coords ?? {}),
  });
}

export async function fetchStaffPayouts(token: string) {
  return staffApi<StaffPayoutsResponse>('GET', '/staff/payouts', token);
}

export async function continueStaffConnectedAccountOnboarding(token: string, connectedAccountId: string) {
  return staffApi<StaffConnectedAccountOnboardingResponse>(
    'POST',
    `/staff/connected-accounts/${encodeURIComponent(connectedAccountId)}/onboarding`,
    token,
    {},
  );
}

export type KitchenJobStatus = 'NEW' | 'IN_PROGRESS' | 'READY' | 'PICKED_UP' | 'CANCELLED' | 'NO_COOK';
export type KitchenProductionJob = {
  id: string;
  eventType: 'ORDER_ITEM' | 'COURSE_FIRE' | 'NO_COOK';
  status: KitchenJobStatus | 'NO_COOK';
  tableLabel: string | null;
  posTableId: string | null;
  orderId: string | null;
  shortOrderNumber: string | null;
  productName: string;
  quantity: number;
  course: number | null;
  modifiers: unknown;
  comment: string | null;
  allergyCodes: unknown;
  dispatchGroupId: string | null;
  dispatchGroupCode: string | null;
  dispatchGroupName: string | null;
  source?: string | null;
  orderedAt: string;
  firedAt: string | null;
  readyAt: string | null;
  pickedUpAt: string | null;
  cancelledAt: string | null;
  metadata: unknown;
};

export type KitchenProductionJobsResponse = {
  serverTime: string;
  mode?: 'PLATFORM_POS' | 'PARALLEL_TO_EXTERNAL_POS' | 'PERIPHERALS_DISABLED';
  jobs: KitchenProductionJob[];
  groups: Array<{ id: string; code: string; name: string; sortOrder: number }>;
};

export type KitchenStopListProduct = {
  id: string;
  name: string;
  posProductId: string | null;
  dispatchGroupId: string | null;
  dispatchGroupName: string | null;
  isStopped: boolean;
  platformManualStopped?: boolean;
  syrveStopped?: boolean;
  availabilitySources?: Array<{
    source: 'SYRVE_STOP_LIST' | 'PLATFORM_MANUAL';
    isStopped: boolean;
    balance: number | null;
    updatedAt: string | null;
    terminalGroupId: string | null;
    editable: boolean;
  }>;
  combinedAvailability?: {
    isAvailable: boolean;
    blockedBy: string[];
  };
  balance: number | null;
  reason: string | null;
  updatedByStaffId: string | null;
  updatedAt: string | null;
  expiresAt: string | null;
};

export async function fetchKitchenProductionJobs(token: string, params?: {
  includeHistory?: boolean;
  dispatchGroupId?: string | null;
  course?: number | null;
}) {
  const qs = new URLSearchParams();
  if (params?.includeHistory) qs.set('includeHistory', 'true');
  if (params?.dispatchGroupId) qs.set('dispatchGroupId', params.dispatchGroupId);
  if (params?.course) qs.set('course', String(params.course));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return staffApi<KitchenProductionJobsResponse>('GET', `/staff/kitchen/production-jobs${suffix}`, token);
}

export async function updateKitchenProductionJobStatus(token: string, jobId: string, toStatus: KitchenJobStatus) {
  return staffApi<{ ok: boolean; job: { id: string; status: KitchenJobStatus } }>(
    'POST',
    `/staff/kitchen/production-jobs/${encodeURIComponent(jobId)}/status`,
    token,
    { toStatus },
  );
}

export async function fetchKitchenStopListProducts(token: string) {
  return staffApi<{ products: KitchenStopListProduct[] }>('GET', '/staff/kitchen/stop-list/products', token);
}

export async function setKitchenStopListProduct(token: string, productId: string, body: {
  isStopped: boolean;
  balance?: number | null;
  reason?: string | null;
  expiresAt?: string | null;
}) {
  return staffApi<{ ok: boolean }>(
    'POST',
    `/staff/kitchen/stop-list/products/${encodeURIComponent(productId)}`,
    token,
    body,
  );
}

export async function fetchCurrentStaffServiceShift(token: string) {
  return staffApi<StaffCurrentServiceShiftResponse>('GET', '/staff/service-shifts/current', token);
}

export async function openStaffServiceShift(params: {
  token: string;
  eventId?: string | null;
  serviceDate?: string | null;
  openingCashBalances: StaffServiceShiftOpeningCashBalance[];
}) {
  return staffApi<StaffServiceShiftRecord>('POST', '/staff/service-shifts/open', params.token, {
    eventId: params.eventId ?? undefined,
    serviceDate: params.serviceDate ?? undefined,
    openingCashBalances: params.openingCashBalances,
  });
}

export type StaffModifierProduct = {
  id: string;
  name: string;
  priceMinor?: number | null;
  priceWithVatMinor?: number | null;
  vatRate?: number | null;
  posProductId?: string | null;
  posGroupId?: string | null;
};

export type StaffModifierGroup = {
  groupId: string;
  required: boolean;
  minAmount: number;
  maxAmount: number | null;
  childModifierIds: string[];
  childModifiers?: Array<{
    productId: string;
    posProductId: string | null;
    name: string;
    priceMinor: number | null;
    priceWithVatMinor: number | null;
    vatRate: number | null;
  }>;
};

export type StaffModifierConfig = { groups: StaffModifierGroup[] } | null;

export type StaffPosTablesResponse = {
  halls: Array<{
    hallId: string;
    hallName: string | null;
    sections: Array<{
      sectionId: string;
      sectionName: string | null;
      tables: Array<{
        id: string;
        name: string | null;
        number: string | null;
        seatingCapacity: number | null;
        isDeleted: boolean;
        placeId?: string | null;
        guestToken?: string | null;
        placeName?: string | null;
        claimedByPartyId?: string | null;
        claimedByPartyName?: string | null;
        claimedByMe?: boolean;
        claimedAt?: string | null;
        claimedByActive?: boolean;
        visitStartedAt?: string | null;
        visitStartSource?: string | null;
        visitStartReservationId?: string | null;
        firstOrderSignalAt?: string | null;
        firstOrderSignalSource?: string | null;
        firstOrderSignalRefId?: string | null;
        hasReleasePendingRetry?: boolean;
        releasePendingMessage?: string | null;
        hasAttentionItems?: boolean;
        hasOverdueItems?: boolean;
        attentionItemsCount?: number;
        overdueItemsCount?: number;
        guestReadyElapsedSec?: number | null;
        guestReadyAttention?: boolean;
        guestReadyOverdue?: boolean;
        hasPendingRequest?: boolean;
        pendingRequestCount?: number;
        hasApprovedCart?: boolean;
        approvedCartItemsCount?: number;
        hasCurrentVisitPendingPreorders?: boolean;
        pendingRequestCountCurrentVisit?: number;
        preorderPendingAttention?: boolean;
        preorderPendingOverdue?: boolean;
        scheduledVisitPaidRequestCount?: number;
        hasActivePayment?: boolean;
        isRecentClosed?: boolean;
        occupancyState?: 'open' | 'paid' | 'closing' | 'free';
        openOrdersCount?: number;
        itemsCount?: number;
        orderSnippets?: Array<{ id: string; label: string; itemsCount: number }>;
        extraOrdersCount?: number;
      }>;
    }>;
  }>;
};

export async function fetchStaffPosTables(token: string, merchantId?: string) {
  const qs = merchantId ? `?merchantId=${encodeURIComponent(merchantId)}` : '';
  return staffApi<StaffPosTablesResponse>('GET', `/staff/pos/tables${qs}`, token);
}

export async function restartStaffTableGuestReadyTimer(params: { token: string; posTableId: string; merchantId?: string }) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{
    ok: boolean;
    meta: {
      visitStartedAt?: string | null;
      visitStartSource?: string | null;
      firstOrderSignalAt?: string | null;
      firstOrderSignalSource?: string | null;
      firstOrderSignalRefId?: string | null;
      revision?: number | null;
      updatedAt?: string | null;
    };
  }>('POST', `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/restart-timer${qs}`, params.token);
}

export type StaffOpenOrdersResponse = {
  coursePosItemMappings?: Array<{
    id: string;
    course: number;
    posItemId: string;
    label?: string | null;
    isActive: boolean;
    readinessBlockers?: string[];
  }>;
  tableMeta?: {
    partyGuestCount: number | null;
    partyDetails: StaffPartyDetails | null;
    visitStartedAt?: string | null;
    visitStartSource?: string | null;
    visitStartReservationId?: string | null;
    firstOrderSignalAt?: string | null;
    firstOrderSignalSource?: string | null;
    firstOrderSignalRefId?: string | null;
    loyaltyCardMasked?: string | null;
    loyaltyCustomerId?: string | null;
    loyaltyLookupType?: 'phone' | 'cardNumber' | null;
    loyaltyLookupMasked?: string | null;
    loyaltySource?: 'guest' | 'staff' | null;
    loyaltyUpdatedAt?: string | null;
    itemAttentionAfterSec?: number;
    itemOverdueAfterSec?: number;
    guestReadyAttentionSec?: number;
    guestReadyOverdueSec?: number;
  } | null;
  orders: Array<{
    id: string | null;
    status: string | null;
    number: string | null;
    openedAt: string | null;
    closedAt?: string | null;
    waiter: { id: string | null; name: string | null } | null;
    total: number | null;
    items: Array<{
      id: string | null;
      itemKey?: string;
      positionId?: string | null;
      productPosId?: string | null;
      name: string | null;
      quantity: number | null;
      course?: number | null;
      requestId?: string | null;
      assignedGuestRef?: string | null;
      lineId?: string | null;
      guestOrdinal?: number | null;
      guestDisplayName?: string | null;
      guestLabel?: string | null;
      allergyCodes?: string[] | null;
      price: number | null;
      resultSum: number | null;
      comment?: string | null;
      isDeleted?: boolean;
      statusChangedAt?: string | null;
      readySource?: 'AUTO_TIMER' | 'POS' | 'MANUAL' | null;
      elapsedStatusSec?: number | null;
      noCookAdjustments?: Array<{
        id: string;
        mode: 'NO_COOK_NO_CHARGE' | 'NO_COOK_BILLABLE';
        quantity: number;
        amountMinor?: number | null;
        reasonCode: string;
        reasonLabel: string;
        approvalStatus: string;
        posSyncStatus: string;
        kitchenDispatchStatus: string;
      }>;
      noCookNoChargeQuantity?: number;
      noCookNoChargeAmountMinor?: number;
      isWrittenOff?: boolean;
      modifiers?: Array<{
        id: string | null;
        productPosId?: string | null;
        name: string | null;
        assignedGuestRef?: string | null;
        guestOrdinal?: number | null;
        guestDisplayName?: string | null;
        guestLabel?: string | null;
        quantity: number | null;
        price: number | null;
        resultSum: number | null;
        comment?: string | null;
        isDeleted?: boolean;
        noCookNoChargeQuantity?: number;
        noCookNoChargeAmountMinor?: number;
        isWrittenOff?: boolean;
      }>;
      status?: string | null;
    }>;
    source?: string | null;
    assignedStaffPartyId?: string | null;
    createdByStaffPartyId?: string | null;
    revision?: number | null;
    isLocked?: boolean | null;
    lockedAt?: string | null;
    precheckPrintedAt?: string | null;
    lockReason?: string | null;
    guestName?: string | null;
    lastItemAddedByPartyId?: string | null;
    lastItemAddedByName?: string | null;
    lastItemAddedByKind?: 'STAFF' | 'GUEST' | 'POS' | 'UNKNOWN' | null;
    uiState?: {
      isPayable: boolean;
      isSelectable: boolean;
      payableAmount: number;
      remainingAmount: number;
      statusLabel: string;
      status: string | null;
      sortTimestamp: string | null;
      isRecentClosed: boolean;
      isDeleted: boolean;
    };
  }>;
};

export type StaffSubmitOrderResponse = StaffOpenOrdersResponse & {
  orderId?: string | null;
  orderIds?: string[] | null;
  idempotent?: boolean | null;
};

export type StaffStationCompositionStatus =
  | 'DRAFT'
  | 'SENT'
  | 'PRODUCTION_NEW'
  | 'PRODUCTION_IN_PROGRESS'
  | 'READY'
  | 'SERVED'
  | 'PRECHECK_LOCKED'
  | 'PAYMENT_IN_PROGRESS'
  | 'PAID'
  | 'FISCAL_PENDING'
  | 'FISCALIZED'
  | 'VOIDED';

export type StaffStationOrderCompositionItem = {
  rowId: string;
  itemKey: string | null;
  positionId: string | null;
  name: string;
  quantity: number;
  course: number | null;
  guestOrdinal: number | null;
  guestLabel: string | null;
  allergyCodes: string[];
  comment: string | null;
  modifiers: Array<{
    name: string;
    quantity: number;
    comment: string | null;
  }>;
  unitPriceMinor: number | null;
  totalMinor: number | null;
  status: StaffStationCompositionStatus;
  availability: 'AVAILABLE' | 'READ_ONLY' | 'LOCKED';
  noCookAdjustments?: Array<{
    id: string;
    mode: 'NO_COOK_NO_CHARGE' | 'NO_COOK_BILLABLE';
    quantity: number;
    reasonCode: string;
    reasonLabel: string;
    approvalStatus: string;
    posSyncStatus: string;
    kitchenDispatchStatus: string;
  }>;
};

export type StaffStationOrderCompositionOrder = {
  id: string;
  number: string | null;
  label: string;
  openedAt: string | null;
  status: string | null;
  compositionStatus: StaffStationCompositionStatus;
  itemCount: number;
  visibleItemCount: number;
  hiddenInternalLineCount: number;
  totalMinor: number | null;
  remainingAmountMinor: number | null;
  paidAmountMinor: number | null;
  isSelectable: boolean;
  isLocked: boolean;
  lockReasons: string[];
  items: StaffStationOrderCompositionItem[];
};

export type StaffStationOrderComposition = {
  table: {
    id: string;
    label: string;
  };
  station: {
    id: string | null;
  };
  revision: string;
  openOrders: StaffStationOrderCompositionOrder[];
  guestPreorders: Array<{
    id: string;
    label: string;
    status: string;
    itemCount: number;
  }>;
  lockSummary: {
    statuses: StaffStationCompositionStatus[];
    reasons: string[];
  };
  capabilities: {
    canViewPrices: boolean;
    canCheckout: boolean;
    canEditSentItems: boolean;
    canBulkProductionStatus: boolean;
  };
};

export type NoCookAdjustmentMode = 'NO_COOK_NO_CHARGE' | 'NO_COOK_BILLABLE';

export async function createStaffStationNoCookAdjustment(params: {
  token: string;
  stationId: string;
  orderId: string;
  itemId: string;
  quantity: number;
  mode: NoCookAdjustmentMode;
  reasonCode: string;
  clientRequestId: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ ok: boolean; adjustment: unknown }>(
    'POST',
    `/staff/stations/${encodeURIComponent(params.stationId)}/orders/${encodeURIComponent(
      params.orderId,
    )}/items/${encodeURIComponent(params.itemId)}/no-cook-adjustments${qs}`,
    params.token,
    {
      quantity: params.quantity,
      mode: params.mode,
      reasonCode: params.reasonCode,
      clientRequestId: params.clientRequestId,
    },
  );
}

export type ManagerWriteOffApproval = {
  id: string;
  table: string | null;
  orderId: string;
  posOrderId: string | null;
  itemName: string;
  quantity: number;
  amountMinor: number;
  currency: string;
  mode: NoCookAdjustmentMode;
  reasonCode: string;
  reasonLabel: string;
  requestedByPartyId: string | null;
  requestedFromStationId: string | null;
  createdAt: string;
};

export async function fetchManagerWriteOffApprovals(token: string) {
  return staffApi<{ approvals: ManagerWriteOffApproval[] }>('GET', '/staff/manager/approvals/write-offs', token);
}

export async function approveManagerWriteOff(token: string, id: string) {
  return staffApi<{ ok: boolean; adjustment: unknown }>(
    'POST',
    `/staff/manager/approvals/write-offs/${encodeURIComponent(id)}/approve`,
    token,
  );
}

export async function rejectManagerWriteOff(token: string, id: string, comment?: string | null) {
  return staffApi<{ ok: boolean; adjustment: unknown }>(
    'POST',
    `/staff/manager/approvals/write-offs/${encodeURIComponent(id)}/reject`,
    token,
    { comment: comment ?? null },
  );
}

export type StaffAiRecommendationGroupId =
  | 'recommended_wines'
  | 'recommended_cocktails'
  | 'recommended_non_alcoholic'
  | 'upsell';

export type StaffAiRecommendationItem = {
  productId: string;
  name: string;
  price: {
    amountMinor: number | null;
    currency: string | null;
  };
  score: number;
  reason_short: string;
  reason_staff: string;
  badges: string[];
};

export type StaffAiRecommendationsResponse = {
  recommendationLogId: string;
  localeUsed: 'en' | 'lv' | 'ru' | 'fr';
  groups: Record<StaffAiRecommendationGroupId, StaffAiRecommendationItem[]>;
};

export type StaffRecommendationEventType = 'shown' | 'clicked' | 'accepted' | 'added_to_order' | 'converted';

export type StaffTrackRecommendationEventResponse = {
  eventId: string;
  recommendationLogId: string;
  eventType: StaffRecommendationEventType;
  action: string;
  recommendedProductId: string;
  orderItemId: string | null;
  surface: 'WAITER_UI' | 'GUEST_UI';
  contextType: 'PRODUCT' | 'CART' | 'CHECKOUT';
  deduplicated: boolean;
  linkedOrderItemBackfilled: boolean;
  createdAt: string;
};

export async function fetchStaffAiRecommendations(params: {
  token: string;
  placeId: string;
  posTableId: string;
  orderId?: string | null;
  selectedProductId?: string | null;
  draftGuestRef?: string | null;
  draftProductIds?: string[] | null;
  locale?: string | null;
  limit?: number | null;
  merchantId?: string;
}) {
  const query = new URLSearchParams();
  query.set('placeId', params.placeId);
  query.set('posTableId', params.posTableId);
  if (params.orderId) query.set('orderId', params.orderId);
  if (params.selectedProductId) query.set('selectedProductId', params.selectedProductId);
  if (params.draftGuestRef) query.set('draftGuestRef', params.draftGuestRef);
  if (Array.isArray(params.draftProductIds)) {
    params.draftProductIds
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry) => entry.length > 0)
      .forEach((entry) => query.append('draftProductIds', entry));
  }
  if (params.locale) query.set('locale', params.locale);
  if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
    query.set('limit', String(Math.floor(params.limit)));
  }
  if (params.merchantId) query.set('merchantId', params.merchantId);
  const qs = query.toString();
  return staffApi<StaffAiRecommendationsResponse>(
    'GET',
    `/staff/ai-recommendations${qs ? `?${qs}` : ''}`,
    params.token,
  );
}

export async function trackStaffRecommendationEvent(params: {
  token: string;
  recommendationLogId: string;
  recommendedProductId: string;
  eventType: StaffRecommendationEventType;
  idempotencyKey?: string | null;
  orderItemId?: string | null;
  surface?: 'WAITER_UI' | 'GUEST_UI' | null;
  contextType?: 'PRODUCT' | 'CART' | 'CHECKOUT' | null;
}) {
  return staffApi<StaffTrackRecommendationEventResponse>(
    'POST',
    `/staff/ai-recommendations/${encodeURIComponent(params.recommendationLogId)}/events`,
    params.token,
    {
      eventType: params.eventType,
      recommendedProductId: params.recommendedProductId,
      idempotencyKey: params.idempotencyKey ?? null,
      orderItemId: params.orderItemId ?? null,
      surface: params.surface ?? null,
      contextType: params.contextType ?? null,
    },
  );
}

export async function fetchStaffTableOrders(token: string, posTableId: string, merchantId?: string) {
  const qs = merchantId ? `?merchantId=${encodeURIComponent(merchantId)}` : '';
  return staffApi<StaffOpenOrdersResponse>('GET', `/staff/pos/tables/${encodeURIComponent(posTableId)}/orders${qs}`, token);
}

export async function fetchStaffStationTableWorkspace(params: {
  token: string;
  stationId: string;
  posTableId: string;
  merchantId?: string;
  tableLabel?: string | null;
}) {
  const qs = new URLSearchParams();
  if (params.merchantId) qs.set('merchantId', params.merchantId);
  if (params.tableLabel) qs.set('tableLabel', params.tableLabel);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return staffApi<{ orders: StaffOpenOrdersResponse; composition: StaffStationOrderComposition }>(
    'GET',
    `/staff/stations/${encodeURIComponent(params.stationId)}/tables/${encodeURIComponent(params.posTableId)}/workspace${suffix}`,
    params.token,
  );
}

export async function fetchStaffStationTableComposition(params: {
  token: string;
  stationId: string;
  posTableId: string;
  merchantId?: string;
  tableLabel?: string | null;
}) {
  const qs = new URLSearchParams();
  if (params.merchantId) qs.set('merchantId', params.merchantId);
  if (params.tableLabel) qs.set('tableLabel', params.tableLabel);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return staffApi<StaffStationOrderComposition>(
    'GET',
    `/staff/stations/${encodeURIComponent(params.stationId)}/tables/${encodeURIComponent(params.posTableId)}/composition${suffix}`,
    params.token,
  );
}

export async function fetchStaffStationOrderComposition(params: {
  token: string;
  stationId: string;
  posOrderId: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffStationOrderComposition>(
    'GET',
    `/staff/stations/${encodeURIComponent(params.stationId)}/orders/${encodeURIComponent(params.posOrderId)}/composition${qs}`,
    params.token,
  );
}

export async function forceCloseStaffTableOrdersInDb(params: {
  token: string;
  posTableId: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ ok: boolean; closedCount: number; closedOrderLabels: string[] }>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/orders/force-close-db${qs}`,
    params.token,
  );
}

export async function updateStaffOrderItemStatus(params: {
  token: string;
  posTableId: string;
  posOrderId: string;
  positionId: string;
  toStatus: 'READY' | 'SERVED' | 'CANCELED';
  idempotencyKey: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{
    ok: boolean;
    item: {
      itemKey: string;
      status: string;
      statusChangedAt: string;
      readySource: string | null;
      servedAt: string | null;
      canceledAt: string | null;
    };
  }>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/orders/${encodeURIComponent(
      params.posOrderId,
    )}/items/${encodeURIComponent(params.positionId)}/status${qs}`,
    params.token,
    {
      toStatus: params.toStatus,
      idempotencyKey: params.idempotencyKey,
    },
  );
}

export type StaffOrderItemIssuePreset =
  | 'DELAY_5'
  | 'DELAY_10'
  | 'DELAY_20'
  | 'OUT_OF_STOCK'
  | 'NEEDS_MANAGER'
  | 'WRONG_ITEM';

export async function recordStaffOrderItemIssue(params: {
  token: string;
  posTableId: string;
  posOrderId: string;
  positionId: string;
  preset: StaffOrderItemIssuePreset;
  idempotencyKey: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{
    ok: boolean;
    preset: StaffOrderItemIssuePreset;
    delayMinutes: number | null;
    action: 'DELAY_PRESET' | 'ISSUE_PRESET';
  }>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/orders/${encodeURIComponent(
      params.posOrderId,
    )}/items/${encodeURIComponent(params.positionId)}/issue${qs}`,
    params.token,
    {
      preset: params.preset,
      idempotencyKey: params.idempotencyKey,
    },
  );
}

export async function updateStaffTableMeta(params: {
  token: string;
  posTableId: string;
  merchantId?: string;
  partyGuestCount?: number | null;
  partyDetails?: StaffPartyDetails | null;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ meta: StaffOpenOrdersResponse['tableMeta'] }>(
    'PATCH',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/meta${qs}`,
    params.token,
    { partyGuestCount: params.partyGuestCount, partyDetails: params.partyDetails },
  );
}

export type StaffLoyaltyCardResponse = {
  posTableId: string;
  loyalty: {
    hasLoyaltyCard: boolean;
    loyaltyCardMasked: string | null;
    loyaltyCustomerId?: string | null;
    loyaltyLookupType?: 'phone' | 'cardNumber' | null;
    loyaltyLookupMasked?: string | null;
    loyaltySource: 'guest' | 'staff' | null;
    loyaltyUpdatedAt: string | null;
  };
};

export async function setStaffTableLoyaltyCard(params: {
  token: string;
  posTableId: string;
  lookupValue: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffLoyaltyCardResponse>(
    'PUT',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/loyalty-card${qs}`,
    params.token,
    { lookupValue: params.lookupValue },
  );
}

export async function clearStaffTableLoyaltyCard(params: {
  token: string;
  posTableId: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffLoyaltyCardResponse>(
    'DELETE',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/loyalty-card${qs}`,
    params.token,
  );
}

export async function updateStaffPosOrderMeta(params: {
  token: string;
  posOrderId: string;
  posTableId: string;
  merchantId?: string;
  guestName: string | null;
}) {
  return staffApi<{ ok: boolean; guestName: string | null }>(
    'PATCH',
    `/staff/pos/orders/${encodeURIComponent(params.posOrderId)}/meta`,
    params.token,
    {
      posTableId: params.posTableId,
      merchantId: params.merchantId,
      guestName: params.guestName,
    },
  );
}

export async function updateStaffGuestBucketMeta(params: {
  token: string;
  posTableId: string;
  assignedGuestRef: string;
  merchantId?: string;
  guestLabel?: string | null;
  guestDisplayName?: string | null;
  guestOrdinal?: number | null;
}) {
  return staffApi<{
    ok: boolean;
    label: {
      assignedGuestRef: string;
      guestOrdinal: number | null;
      guestDisplayName: string | null;
      guestLabel: string | null;
      updatedAt: string;
    };
  }>(
    'PATCH',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/guest-buckets/meta`,
    params.token,
    {
      merchantId: params.merchantId,
      assignedGuestRef: params.assignedGuestRef,
      guestLabel: params.guestLabel ?? null,
      guestDisplayName: params.guestDisplayName ?? null,
      guestOrdinal: params.guestOrdinal ?? null,
    },
  );
}

export type StaffPosMenu = {
  items: Array<{
    id: string;
    name: string;
    searchText?: string | null;
    priceMinor?: number | null;
    priceWithVatMinor?: number | null;
    vatRate?: number | null;
    category?: string | null;
    menuCategoryId?: string | null;
    modifierSchemaId?: string | null;
    modifierConfig?: StaffModifierConfig;
    isOnMenu?: boolean | null;
    isDeleted?: boolean | null;
    stopList?: {
      balance: number;
      dateAdd?: string | null;
      isStopped: boolean;
      terminalGroupId?: string | null;
      stopListIds?: string[];
    } | null;
  }>;
  categories?: Array<{ id: string; name: string; sortOrder: number | null }>;
  externalMenuId?: string | null;
  externalMenuName?: string | null;
  menuVersionUsed?: number | null;
  autoResolvedExternalMenu?: boolean;
  topPicks?: {
    items: Array<{
      id: string;
      name: string;
      priceMinor?: number | null;
      priceWithVatMinor?: number | null;
      vatRate?: number | null;
      category?: string | null;
      menuCategoryId?: string | null;
      modifierConfig?: StaffModifierConfig;
      stopList?: StaffPosMenu["items"][number]["stopList"];
    }>;
    strategy?: string;
    appliedMenuCategoryIds?: string[];
    fallbackUsed?: boolean;
    emptyResultReason?: "category_filter_excluded_all" | "no_payable_menu_items" | null;
  };
};

export async function fetchStaffPosMenu(
  token: string,
  merchantId?: string | null,
  locale?: string | null,
  posTableId?: string | null,
) {
  const params = new URLSearchParams();
  if (merchantId) params.set("merchantId", merchantId);
  if (locale) params.set("locale", locale);
  if (posTableId) params.set("posTableId", posTableId);
  const qs = params.toString().length ? `?${params.toString()}` : "";
  return staffApi<{ menu: StaffPosMenu; modifierProducts?: StaffModifierProduct[] }>(
    'GET',
    `/staff/pos/menu${qs}`,
    token,
  );
}

export type StaffOrderItemInput = {
  productId: string;
  quantity: number;
  course?: number;
  comment?: string;
  requestId?: string | null;
  assignedGuestRef?: string | null;
  lineId?: string | null;
  guestOrdinal?: number | null;
  guestDisplayName?: string | null;
  guestLabel?: string | null;
  allergyCodes?: string[] | null;
  __targetPosOrderId?: string | null;
  modifiers?: Array<{
    productId: string;
    quantity: number;
    name?: string | null;
    priceMinor?: number | null;
    priceWithVatMinor?: number | null;
    vatRate?: number | null;
  }>;
};

export async function submitStaffTableItems(params: {
  token: string;
  posTableId: string;
  items: StaffOrderItemInput[];
  idempotencyKey: string;
  merchantId?: string;
  mode?: 'add' | 'create' | 'new' | 'existing';
  targetPosOrderId?: string | null;
  requestIds?: string[];
  tableCapacity?: number | null;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffSubmitOrderResponse>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/orders/create-or-add-items${qs}`,
    params.token,
    {
      items: params.items,
      mode: params.mode,
      targetPosOrderId: params.targetPosOrderId ?? undefined,
      requestIds: Array.isArray(params.requestIds) ? params.requestIds : undefined,
      tableCapacity: params.tableCapacity ?? undefined,
    },
    { 'Idempotency-Key': params.idempotencyKey },
  );
}

export async function fireStaffTableCourse(params: {
  token: string;
  posTableId: string;
  course: number;
  merchantId?: string;
  idempotencyKey: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{
    ok: boolean;
    idempotent: boolean;
    posOrderId: string | null;
    orders: StaffOpenOrdersResponse['orders'];
    tableMeta?: StaffOpenOrdersResponse['tableMeta'];
    coursePosItemMappings?: StaffOpenOrdersResponse['coursePosItemMappings'];
  }>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/fire-course${qs}`,
    params.token,
    { course: params.course },
    { 'Idempotency-Key': params.idempotencyKey },
  );
}

export type StaffReservation = {
  id: string;
  placeId: string | null;
  posTableId: string | null;
  guestName: string;
  partySize: number | null;
  startsAt: string;
  endsAt: string;
  status: string;
  code: string | null;
  source: string;
  externalId?: string | null;
  hasScheduledPreorders?: boolean;
};

export type StaffReservationCancelReason = {
  id: string;
  text: string;
  requiresText: boolean;
};

export async function fetchStaffReservationCancelReasons(params: {
  token: string;
  merchantId?: string | null;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ reasons: StaffReservationCancelReason[] }>(
    'GET',
    `/staff/reservations/cancel-reasons${qs}`,
    params.token,
  );
}

export async function deleteStaffReservation(params: {
  token: string;
  reservationId: string;
  merchantId?: string | null;
  cancelReasonId?: string | null;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ reservation: StaffReservation }>(
    'DELETE',
    `/staff/reservations/${encodeURIComponent(params.reservationId)}${qs}`,
    params.token,
    params.cancelReasonId ? { cancelReasonId: params.cancelReasonId } : undefined,
  );
}

export type StaffTableOccupyPreview = {
  now: string;
  window: { from: string; to: string };
  activeReservation: {
    id: string;
    guestName: string;
    partySize: number | null;
    startsAt: string;
    endsAt: string;
    code: string | null;
    externalId: string | null;
    posTableId: string;
    tableLabel: string;
  } | null;
  relocationTargets: Array<{
    posTableId: string;
    placeId: string;
    tableLabel: string;
    restoplaceItemId: string;
  }>;
  occupancyDurationMinutes: number;
  addItemOnBookingEnabled: boolean;
  relocationAvailable: boolean;
  relocationUnavailableReason: string | null;
};

export type StaffTableOccupyResponse = {
  ok: true;
  decision: 'no_active_reservation' | 'match_existing' | 'relocate_existing' | 'select_reservation';
  reservation: {
    id: string;
    posTableId: string;
    startsAt: string;
    endsAt: string;
    source: string;
  };
  visitStart: {
    id: string;
    startedAt: string;
    source: string;
  };
  bookingMarker: {
    attempted: boolean;
    status: 'skipped';
    error: string | null;
  };
  sync?: {
    restoplaceAttempted: boolean;
    restoplaceSynced: boolean;
    needsRetry: boolean;
    errorCode: string | null;
    errorMessage: string | null;
  };
  idempotent?: boolean;
};

export async function fetchStaffReservations(
  token: string,
  params?: {
    date?: string | null;
    scope?: 'day' | 'upcoming';
    includeUnassigned?: boolean;
    posTableId?: string | null;
    from?: string | null;
    to?: string | null;
    limit?: number | null;
    merchantId?: string | null;
  },
) {
  const search = new URLSearchParams();
  if (params?.date) search.set('date', params.date);
  if (params?.scope) search.set('scope', params.scope);
  if (params?.includeUnassigned) search.set('includeUnassigned', 'true');
  if (params?.posTableId) search.set('posTableId', params.posTableId);
  if (params?.from) search.set('from', params.from);
  if (params?.to) search.set('to', params.to);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.merchantId) search.set('merchantId', params.merchantId);
  const qs = search.toString() ? `?${search.toString()}` : '';
  return staffApi<{ reservations: StaffReservation[] }>('GET', `/staff/reservations${qs}`, token);
}

export async function fetchStaffTableOccupyPreview(params: {
  token: string;
  posTableId: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffTableOccupyPreview>(
    'GET',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/occupy-preview${qs}`,
    params.token,
  );
}

export async function occupyStaffTableNow(params: {
  token: string;
  posTableId: string;
  decision: 'no_active_reservation' | 'match_existing' | 'relocate_existing' | 'select_reservation';
  idempotencyKey: string;
  merchantId?: string;
  activeReservationId?: string | null;
  reservationId?: string | null;
  scheduledPreorderMode?: 'now' | 'then' | null;
  relocateTargetPosTableId?: string | null;
}) {
  return staffApi<StaffTableOccupyResponse>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/occupy`,
    params.token,
    {
      merchantId: params.merchantId,
      decision: params.decision,
      idempotencyKey: params.idempotencyKey,
      activeReservationId: params.activeReservationId ?? null,
      reservationId: params.reservationId ?? null,
      scheduledPreorderMode: params.scheduledPreorderMode ?? null,
      relocateTargetPosTableId: params.relocateTargetPosTableId ?? null,
    },
  );
}

export type StaffGuestRequest = {
  id: string;
  posTableId: string;
  guestName?: string | null;
  targetPosOrderId?: string | null;
  assignedGuestRef?: string | null;
  guestOrdinal?: number | null;
  guestDisplayName?: string | null;
  guestLabel?: string | null;
  items: Array<{
    lineId?: string | null;
    productId?: string | null;
    name?: string | null;
    quantity?: number | null;
    course?: number | null;
    comment?: string | null;
    assignedGuestRef?: string | null;
    guestOrdinal?: number | null;
    guestDisplayName?: string | null;
    guestLabel?: string | null;
    allergyCodes?: string[] | null;
    priceMinor?: number | null;
    priceWithVatMinor?: number | null;
    lineTotalMinor?: number | null;
    modifiers?: Array<{
      productId?: string | null;
      quantity?: number | null;
      name?: string | null;
      priceMinor?: number | null;
      priceWithVatMinor?: number | null;
      vatRate?: number | null;
    }>;
  }>;
  status: 'PENDING' | 'APPROVED' | 'RESOLVED' | 'REJECTED' | 'CANCELED';
  dedupeKey?: string | null;
  scheduledVisitPaid?: boolean;
  scheduledVisitDraftId?: string | null;
  scheduledVisit?: {
    id: string;
    guestName: string | null;
    partySize: number | null;
    visitAt: string;
    timezone: string | null;
    status: string;
    fireAt: string | null;
    autoFireEnabled: boolean;
  } | null;
};

export async function fetchStaffGuestRequests(token: string, posTableId: string, merchantId?: string) {
  const qs = merchantId ? `?merchantId=${encodeURIComponent(merchantId)}` : '';
  return staffApi<{ requests: StaffGuestRequest[] }>(
    'GET',
    `/staff/pos/tables/${encodeURIComponent(posTableId)}/requests${qs}`,
    token,
  );
}

export async function upsertStaffWaiterDraftCart(params: {
  token: string;
  posTableId: string;
  items: Array<{
    lineId?: string | null;
    productId?: string | null;
    name?: string | null;
    quantity?: number | null;
    course?: number | null;
    comment?: string | null;
    allergyCodes?: string[] | null;
    priceMinor?: number | null;
    priceWithVatMinor?: number | null;
    lineTotalMinor?: number | null;
    modifiers?: Array<{
      productId?: string | null;
      quantity?: number | null;
      name?: string | null;
      priceMinor?: number | null;
      priceWithVatMinor?: number | null;
      vatRate?: number | null;
    }>;
  }>;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ request: StaffGuestRequest | null }>(
    'PUT',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/draft-cart${qs}`,
    params.token,
    { items: params.items },
  );
}

export async function approveStaffGuestRequest(params: { token: string; requestId: string; merchantId?: string }) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ request: StaffGuestRequest; approvedNow?: boolean }>(
    'POST',
    `/staff/guest-cart-requests/${encodeURIComponent(params.requestId)}/approve${qs}`,
    params.token,
  );
}

export async function resolveStaffGuestRequestsForTable(params: {
  token: string;
  posTableId: string;
  requestIds?: string[];
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ resolved: number }>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/requests/resolve${qs}`,
    params.token,
    { requestIds: params.requestIds ?? undefined },
  );
}

export async function patchStaffGuestRequest(params: {
  token: string;
  requestId: string;
  items: Array<{
    lineId: string;
    quantity?: number | null;
    course?: number | null;
    allergyCodes?: string[] | null;
    comment?: string | null;
  }>;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ request: StaffGuestRequest }>(
    'PATCH',
    `/staff/guest-cart-requests/${encodeURIComponent(params.requestId)}${qs}`,
    params.token,
    { items: params.items },
  );
}

export async function removeStaffGuestRequestItems(params: {
  token: string;
  requestId: string;
  lineIds: string[];
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ request: StaffGuestRequest }>(
    'DELETE',
    `/staff/guest-cart-requests/${encodeURIComponent(params.requestId)}/items${qs}`,
    params.token,
    { lineIds: params.lineIds },
  );
}

export async function rejectStaffGuestRequest(params: { token: string; requestId: string; reason?: string | null; merchantId?: string }) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ request: StaffGuestRequest }>(
    'POST',
    `/staff/guest-cart-requests/${encodeURIComponent(params.requestId)}/reject${qs}`,
    params.token,
    { reason: params.reason ?? null },
  );
}

export async function cancelStaffPrepaidGuestRequest(params: {
  token: string;
  requestId: string;
  reason?: string | null;
  merchantId?: string;
  idempotencyKey?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{
    request: StaffGuestRequest;
    draft: { id: string; status: string; amountPaidMinor: number; canceledAt: string | Date | null } | null;
    refundedPayments: Array<{ preorderPaymentId: string; processor: string | null; refundId: string; status: string }>;
    alreadyCanceled?: boolean;
    refundOperationId?: string;
  }>(
    'POST',
    `/staff/guest-cart-requests/${encodeURIComponent(params.requestId)}/cancel-prepaid${qs}`,
    params.token,
    { reason: params.reason ?? null, merchantId: params.merchantId },
    params.idempotencyKey ? { 'Idempotency-Key': params.idempotencyKey } : undefined,
  );
}

export async function createStaffReservation(
  token: string,
  payload: {
    guestName: string;
    startsAt: string;
    endsAt: string;
    code?: string | null;
    partySize?: number | null;
    posTableId?: string | null;
    placeId?: string | null;
    merchantId?: string | null;
  },
) {
  return staffApi<{ reservation: StaffReservation }>('POST', '/staff/reservations', token, payload);
}

export type StaffOrderActivityResponse = {
  items: Array<{
    id: string;
    createdAt: string;
    actor: { partyId: string; name?: string | null };
    action: string;
    summary: string | null;
    details?: unknown;
    idempotencyKey?: string | null;
    syrveCorrelationId?: string | null;
  }>;
  nextCursor?: string | null;
};

export async function fetchStaffOrderActivity(token: string, orderId: string, cursor?: string | null, limit?: number) {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', String(limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  return staffApi<StaffOrderActivityResponse>('GET', `/staff/orders/${orderId}/activity${qs}`, token);
}

export type StaffResolvePosPlaceResponse = {
  placeId: string;
  merchantId: string;
  posType: string;
  posTableId: string;
  tableName?: string | null;
  locationName?: string | null;
};

export async function resolvePosPlace(token: string, guestToken: string) {
  return staffApi<StaffResolvePosPlaceResponse>('POST', '/staff/pos/places/resolve', token, { token: guestToken });
}

export type StaffPosTableGuestTokenResponse = {
  guestToken: string;
  placeId: string;
  placeName?: string | null;
  isActive?: boolean | null;
};

export async function fetchStaffPosTableGuestToken(token: string, posTableId: string, merchantId?: string) {
  const qs = merchantId ? `?merchantId=${encodeURIComponent(merchantId)}` : '';
  return staffApi<StaffPosTableGuestTokenResponse>(
    'GET',
    `/staff/pos/tables/${encodeURIComponent(posTableId)}/guest-token${qs}`,
    token,
  );
}

export type StaffCheckoutSessionResponse = {
  sessionId: string;
  guestPayQrUrl: string;
  remainingAmount: number;
  paidBillAmount: number;
  tipAmount: number;
  currency: string;
  status: string;
  finalizationStatus?: string | null;
  finalizationError?: string | null;
  merchantReceiptStatus?: string | null;
  participantReceiptStatus?: unknown;
  buyerDetails?: StaffCheckoutBuyerDetails | null;
  receiptsDeliveredAt?: string | null;
  finalizedAt?: string | null;
  orderIds: string[];
};

export type StaffCheckoutBuyerDetails = {
  companyName?: string;
  registrationNumber?: string;
  vatNumber?: string;
  legalAddress?: string;
  email?: string;
  contactPerson?: string;
};

export async function createStaffCheckoutSession(params: {
  token: string;
  orderIds?: string[];
  allOrders?: boolean;
  posTableId?: string;
  merchantId?: string;
  receiptEmail?: string | null;
  consentEmailReceipt?: boolean;
  consentMarketingUpdates?: boolean;
  buyerDetails?: StaffCheckoutBuyerDetails | null;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  const body = {
    orderIds: params.orderIds,
    allOrders: params.allOrders,
    posTableId: params.posTableId,
    receiptEmail: params.receiptEmail ?? undefined,
    consentEmailReceipt: params.consentEmailReceipt,
    consentMarketingUpdates: params.consentMarketingUpdates,
    buyerDetails: params.buyerDetails ?? undefined,
  };
  return staffApi<StaffCheckoutSessionResponse>('POST', `/staff/pos/checkout/sessions${qs}`, params.token, body);
}

export async function updateStaffCheckoutSessionBuyer(params: {
  token: string;
  sessionId: string;
  merchantId?: string;
  buyerDetails?: StaffCheckoutBuyerDetails | null;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ sessionId: string; buyerDetails: StaffCheckoutBuyerDetails | null }>(
    'PATCH',
    `/staff/pos/checkout/sessions/${encodeURIComponent(params.sessionId)}/buyer${qs}`,
    params.token,
    { buyerDetails: params.buyerDetails ?? null },
  );
}

export async function recordStaffCashPayment(params: {
  token: string;
  sessionId: string;
  baseAmount: number;
  tipAmount?: number;
  idempotencyKey?: string;
  merchantId?: string;
  receiptEmail?: string | null;
  consentEmailReceipt?: boolean;
  consentMarketingUpdates?: boolean;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  const body = {
    baseAmount: params.baseAmount,
    tipAmount: params.tipAmount ?? 0,
    idempotencyKey: params.idempotencyKey,
    receiptEmail: params.receiptEmail ?? undefined,
    consentEmailReceipt: params.consentEmailReceipt,
    consentMarketingUpdates: params.consentMarketingUpdates,
  };
  return staffApi<{ attemptId: string; appliedBaseAmount: number; paymentIds?: string[] }>(
    'POST',
    `/staff/pos/checkout/sessions/${encodeURIComponent(params.sessionId)}/cash${qs}`,
    params.token,
    body,
  );
}

export async function fetchStaffCheckoutSession(params: { token: string; sessionId: string; merchantId?: string }) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffCheckoutSessionResponse>(
    'GET',
    `/staff/pos/checkout/sessions/${encodeURIComponent(params.sessionId)}${qs}`,
    params.token,
  );
}

export async function fetchStaffActiveCheckoutSession(params: {
  token: string;
  posTableId?: string;
  orderIds?: string[];
  merchantId?: string;
}): Promise<StaffCheckoutSessionResponse | null> {
  const search = new URLSearchParams();
  if (params.posTableId) search.set('posTableId', params.posTableId);
  if (params.orderIds?.length) search.set('orderIds', params.orderIds.join(','));
  if (params.merchantId) search.set('merchantId', params.merchantId);
  const qs = search.toString() ? `?${search.toString()}` : '';
  const res = await staffApi<StaffCheckoutSessionResponse | undefined>(
    'GET',
    `/staff/pos/checkout/active-session${qs}`,
    params.token,
  );
  return res ?? null;
}

export async function retryStaffFinalizeSession(params: { token: string; sessionId: string; merchantId?: string }) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{
    sessionId: string;
    finalizationStatus: string | null;
    finalizationError: string | null;
    merchantReceiptStatus: string | null;
    participantReceiptStatus: unknown;
    receiptsDeliveredAt: string | null;
    finalizedAt: string | null;
  }>(
    'POST',
    `/staff/pos/checkout/sessions/${encodeURIComponent(params.sessionId)}/retry-finalize${qs}`,
    params.token,
  );
}

export type StaffVisitCompletionMode = 'online' | 'cash' | 'outside_platform';
export type StaffVisitCompletionStepStatus =
  | 'not_started'
  | 'in_progress'
  | 'success'
  | 'failed'
  | 'pending_retry'
  | 'skipped';
export type StaffVisitCompletionStatus =
  | 'completed'
  | 'completed_with_pos_warning'
  | 'release_pending_retry'
  | 'failed';
export type StaffVisitCompletionRetryStep = 'release' | 'pos_close';
export type StaffVisitSettlementMethod =
  | 'online_already_paid'
  | 'card_terminal'
  | 'cash'
  | 'mixed';
export type StaffVisitSettlementDeclarationStatus =
  | 'not_recorded'
  | 'declared'
  | 'pending_release'
  | 'released'
  | 'release_failed';
export type StaffVisitSettlementBreakdown = {
  onlineMinor: number;
  cardTerminalMinor: number;
  cashMinor: number;
};
export type StaffVisitSettlementDeclaration = {
  method: StaffVisitSettlementMethod;
  amounts: StaffVisitSettlementBreakdown;
  note: string | null;
};

export type StaffVisitCompletionOperation = {
  operationId: string;
  tableId: string;
  mode: StaffVisitCompletionMode;
  status: StaffVisitCompletionStatus;
  stepStatuses: {
    payment: StaffVisitCompletionStepStatus;
    platform_close: StaffVisitCompletionStepStatus;
    pos_close: StaffVisitCompletionStepStatus;
    release: StaffVisitCompletionStepStatus;
  };
  retryableSteps: StaffVisitCompletionRetryStep[];
  idempotencyKey: string;
  correlationId: string;
  settlementDeclarationStatus: StaffVisitSettlementDeclarationStatus;
  settlementDeclaration: StaffVisitSettlementDeclaration | null;
  createdAt: string;
  updatedAt: string;
};

export async function completeStaffVisit(params: {
  token: string;
  posTableId: string;
  mode: StaffVisitCompletionMode;
  orderIds: string[];
  idempotencyKey: string;
  amounts?: {
    baseAmountMinor: number;
    tipAmountMinor: number;
  } | null;
  reason?: string | null;
  paymentSessionId?: string | null;
  settlementDeclaration?: StaffVisitSettlementDeclaration | null;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffVisitCompletionOperation>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/complete-visit${qs}`,
    params.token,
    {
      mode: params.mode,
      orderIds: params.orderIds,
      amounts: params.amounts ?? undefined,
      idempotencyKey: params.idempotencyKey,
      reason: params.reason ?? undefined,
      paymentSessionId: params.paymentSessionId ?? undefined,
      settlementDeclaration: params.settlementDeclaration ?? undefined,
    },
  );
}

export type StaffShiftCloseStatus =
  | 'draft'
  | 'awaiting_cash_retry'
  | 'closed_ok'
  | 'closed_with_mismatch'
  | 'escalated';

export type StaffShiftCloseChannelTotals = {
  onlineMinor: number;
  cardTerminalMinor: number;
  cashMinor: number;
};

export type StaffShiftCloseParticipantRow = {
  participantId: string | null;
  label: string;
  totals: StaffShiftCloseChannelTotals;
};

export type StaffShiftCloseBlockers = {
  openTables: number;
  openOrders: number;
  activeCheckoutSessions: number;
};

export type StaffShiftCloseSession = {
  shiftCloseSessionId: string;
  serviceShiftId: string;
  status: StaffShiftCloseStatus;
  openedCashMinor: number;
  expectedCashMinor: number;
  countedCashMinor: number | null;
  differenceCashMinor: number | null;
  cashMismatchAttempts: number;
  channelTotals: StaffShiftCloseChannelTotals;
  participantRows: StaffShiftCloseParticipantRow[];
  mismatchComment: string | null;
  escalatedAt: string | null;
  canFinalize: boolean;
  closeBlockedCode: string | null;
  closeBlockedMessage: string | null;
  blockers: StaffShiftCloseBlockers;
  createdAt: string;
  updatedAt: string;
};

export type StaffShiftSettlementSummary = {
  merchantId: string;
  serviceShiftId: string;
  totals: StaffShiftCloseChannelTotals;
  operations: Array<{
    operationId: string;
    posTableId: string;
    settlementDeclarationStatus: StaffVisitSettlementDeclarationStatus;
    method: StaffVisitSettlementMethod;
    amounts: StaffShiftCloseChannelTotals;
    createdAt: string;
    updatedAt: string;
    correlationId: string;
  }>;
};

export async function fetchStaffShiftSettlementSummary(params: {
  token: string;
  serviceShiftId: string;
}) {
  return staffApi<StaffShiftSettlementSummary>(
    'GET',
    `/staff/service-shifts/${encodeURIComponent(params.serviceShiftId)}/settlement-summary`,
    params.token,
  );
}

export async function createStaffShiftCloseSession(params: {
  token: string;
  serviceShiftId: string;
}) {
  return staffApi<StaffShiftCloseSession>(
    'POST',
    `/staff/service-shifts/${encodeURIComponent(params.serviceShiftId)}/close-session`,
    params.token,
    {},
  );
}

export async function fetchStaffShiftCloseSession(params: {
  token: string;
  serviceShiftId: string;
}) {
  return staffApi<StaffShiftCloseSession>(
    'GET',
    `/staff/service-shifts/${encodeURIComponent(params.serviceShiftId)}/close-session`,
    params.token,
  );
}

export async function recordStaffShiftCloseCashCount(params: {
  token: string;
  serviceShiftId: string;
  countedCashMinor: number;
  mismatchComment?: string | null;
}) {
  return staffApi<StaffShiftCloseSession>(
    'POST',
    `/staff/service-shifts/${encodeURIComponent(params.serviceShiftId)}/close-session/cash-count`,
    params.token,
    {
      countedCashMinor: params.countedCashMinor,
      mismatchComment: params.mismatchComment ?? undefined,
    },
  );
}

export async function finalizeStaffShiftCloseSession(params: {
  token: string;
  serviceShiftId: string;
  allowMismatch: boolean;
}) {
  return staffApi<StaffShiftCloseSession>(
    'POST',
    `/staff/service-shifts/${encodeURIComponent(params.serviceShiftId)}/close-session/finalize`,
    params.token,
    { allowMismatch: params.allowMismatch },
  );
}

export async function fetchStaffVisitCompletionOperation(params: {
  token: string;
  operationId: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffVisitCompletionOperation>(
    'GET',
    `/staff/pos/visit-completion/${encodeURIComponent(params.operationId)}${qs}`,
    params.token,
  );
}

export async function retryStaffVisitCompletionOperation(params: {
  token: string;
  operationId: string;
  step: StaffVisitCompletionRetryStep;
  idempotencyKey: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffVisitCompletionOperation>(
    'POST',
    `/staff/pos/visit-completion/${encodeURIComponent(params.operationId)}/retry${qs}`,
    params.token,
    {
      step: params.step,
      idempotencyKey: params.idempotencyKey,
    },
  );
}

// Split preview (principal/base_amount) for print check
export type StaffSplitPreviewResponse = {
  currency: string;
  orderIds: string[];
  lines: Array<{
    recipientType: string;
    recipientId: string | null;
    label: string;
    amountMinor: number;
  }>;
  note?: string | null;
};

export async function fetchStaffSplitPreview(params: { token: string; orderIds: string[]; merchantId?: string }) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<StaffSplitPreviewResponse>(
    'POST',
    `/staff/pos/checkout/split-preview${qs}`,
    params.token,
    { orderIds: params.orderIds },
  );
}

export type StaffEvent = {
  id: string;
  posTableId: string;
  orderRef: { orderId?: string | null; posOrderId?: string | null; externalOrderId?: string | null };
  delta: {
    type: string;
    fields?: Record<string, unknown>;
    items?: unknown[];
    lineIds?: string[];
  };
};

export async function pollStaffEvents(token: string, params: { orderId: string; cursor?: string | null }) {
  const search = new URLSearchParams();
  search.append('orderId', params.orderId);
  if (params.cursor) search.append('cursor', params.cursor);
  return staffApi<{ events: StaffEvent[]; nextSince: string | null }>(
    'GET',
    `/staff/pos/events/poll?${search.toString()}`,
    token,
  );
}

export type StaffNotification = {
  id: string;
  type: string;
  payload: StaffNotificationPayload;
  createdAt: string;
};

export async function pollStaffNotifications(token: string, params?: { since?: string | null }) {
  const search = new URLSearchParams();
  if (params?.since) search.append('since', params.since);
  const qs = search.toString();
  const path = qs ? `/staff/notifications/poll?${qs}` : '/staff/notifications/poll';
  // Use staffApi to ensure base URL + proper auth headers and error handling.
  try {
    const res = await staffApi<{ events: StaffNotification[]; nextSince: string | null }>('GET', path, token);
    return res;
  } catch (err: unknown) {
    if (hasApiStatus(err) && (err.status === 304 || err.status === 204)) {
      return { events: [], nextSince: params?.since ?? null };
    }
    throw err;
  }
}

export async function resolveStaffHelpNotifications(token: string, params: { posTableId: string }) {
  return staffApi<{ ok: boolean; resolved: number; signaled: number; recipients: number }>(
    'POST',
    '/staff/notifications/help/resolve',
    token,
    params,
  );
}

export async function printStaffPrecheck(params: {
  token: string;
  posTableId: string;
  posOrderId: string;
  merchantId?: string;
}) {
  const qs = params.merchantId ? `?merchantId=${encodeURIComponent(params.merchantId)}` : '';
  return staffApi<{ ok: boolean; locked: boolean; precheckPrintedAt?: string | null; orders: StaffOpenOrdersResponse['orders'] }>(
    'POST',
    `/staff/pos/tables/${encodeURIComponent(params.posTableId)}/orders/${encodeURIComponent(params.posOrderId)}/print-precheck${qs}`,
    params.token,
  );
}

export type StaffChecklistLoadPreset = 'MINIMUM' | 'MEDIUM' | 'MAXIMUM';
export type StaffChecklistProofRequirement = 'NONE' | 'COMMENT' | 'PHOTO' | 'PHOTO_AND_COMMENT';
export type StaffChecklistRunItemStatus = 'ACTIVE' | 'COMPLETED' | 'SUPERSEDED';
export type StaffChecklistContextKey = 'expectedLoad' | 'weather' | 'dayPart' | 'placeTags' | 'manualTags';
export type StaffChecklistStartContext = {
  expectedLoad?: StaffChecklistLoadPreset;
  weather?: string[];
  dayPart?: string;
  placeTags?: string[];
  manualTags?: string[];
};
export type StaffChecklistContextRequirement = {
  label: string;
  required: boolean;
  options: string[];
};

export type StaffChecklistProof = {
  id: string;
  proofType: 'COMMENT' | 'PHOTO';
  comment: string | null;
  imageUrl: string | null;
  thumbUrl: string | null;
  createdAt: string;
  createdBy: { partyId: string; name: string | null } | null;
};

export type StaffChecklistRunItem = {
  id: string;
  sourceItemId: string;
  title: string;
  sortOrder: number;
  isRequired: boolean;
  proofRequirement: StaffChecklistProofRequirement;
  status: StaffChecklistRunItemStatus;
  completedAt: string | null;
  completedBy: { partyId: string; name: string | null } | null;
  attendanceId: string | null;
  proofs: StaffChecklistProof[];
};

export type StaffChecklistRun = {
  id: string;
  merchantId: string;
  templateId: string;
  templateName: string;
  event: { id: string; name: string };
  role: { id: string; name: string; code: string };
  serviceDate: string;
  context: Record<string, unknown>;
  contextLabel: string | null;
  contextSelectedBy: { partyId: string; name: string | null } | null;
  contextSelectedAt: string | null;
  contextOverriddenBy: { userId: string; name: string | null; email: string | null } | null;
  contextOverriddenAt: string | null;
  status: 'OPEN' | 'COMPLETED' | 'LOCKED' | 'ARCHIVED';
  progress: { completed: number; total: number; completedRequired: number; required: number };
  items: StaffChecklistRunItem[];
};

export type StaffChecklistCurrentResponse =
  | { available: false; reason: 'NO_TEMPLATE' | 'NO_APPLICABLE_ITEMS' }
  | {
      available: true;
      state: 'RUN_OPEN';
      run: StaffChecklistRun;
    }
  | {
      available: true;
      state: 'NEEDS_CONTEXT' | 'READY';
      template: {
        id: string;
        name: string;
        role: { id: string; name: string; code: string };
        event: { id: string; name: string };
        itemCount: number;
        loadOptions: StaffChecklistLoadPreset[];
        defaultContext: Record<string, unknown>;
        contextRequirements?: Record<StaffChecklistContextKey, StaffChecklistContextRequirement>;
        missingContext?: StaffChecklistContextKey[];
      };
    };

export type StaffChecklistUploadAuth = {
  uploadUrl: string;
  publicKey: string;
  token: string;
  expire: number;
  signature: string;
  folder: string;
  fileName: string;
  useUniqueFileName: boolean;
  allowedMimeTypes: string[];
  maxUploadBytes: number;
};

export type StaffChecklistImageKitUploadResult = {
  fileId: string;
  filePath: string;
  url?: string;
  thumbnailUrl?: string;
  size?: number;
  mime?: string;
};

export function fetchCurrentStaffChecklist(token: string) {
  return staffApi<StaffChecklistCurrentResponse>('GET', '/staff/checklists/current', token);
}

export function startStaffChecklist(token: string, templateId: string, context?: StaffChecklistStartContext) {
  return staffApi<StaffChecklistRun>('POST', '/staff/checklists/runs', token, { templateId, context });
}

export function fetchStaffChecklistRun(token: string, runId: string) {
  return staffApi<StaffChecklistRun>('GET', `/staff/checklists/runs/${encodeURIComponent(runId)}`, token);
}

export function requestStaffChecklistProofUploadAuth(
  token: string,
  runId: string,
  runItemId: string,
  payload: { fileName: string; mimeType: string; sizeBytes: number },
) {
  return staffApi<StaffChecklistUploadAuth>(
    'POST',
    `/staff/checklists/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(runItemId)}/proof-upload-auth`,
    token,
    payload,
  );
}

export function completeStaffChecklistItem(
  token: string,
  runId: string,
  runItemId: string,
  payload: {
    comment?: string;
    image?: {
      fileId: string;
      filePath: string;
      originalUrl?: string;
      mimeType?: string;
      sizeBytes?: number;
    };
    images?: {
      fileId: string;
      filePath: string;
      originalUrl?: string;
      mimeType?: string;
      sizeBytes?: number;
    }[];
  } = {},
) {
  return staffApi<StaffChecklistRun>(
    'POST',
    `/staff/checklists/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(runItemId)}/complete`,
    token,
    payload,
  );
}

export function uploadStaffChecklistProofToImageKit(
  auth: StaffChecklistUploadAuth,
  file: File,
  onProgress?: (progress: number) => void,
) {
  return new Promise<StaffChecklistImageKitUploadResult>((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);
    form.append('fileName', auth.fileName);
    form.append('publicKey', auth.publicKey);
    form.append('token', auth.token);
    form.append('expire', String(auth.expire));
    form.append('signature', auth.signature);
    form.append('folder', auth.folder);
    form.append('useUniqueFileName', auth.useUniqueFileName ? 'true' : 'false');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', auth.uploadUrl);
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      let parsed: unknown = null;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        parsed = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && parsed && typeof parsed === 'object') {
        resolve(parsed as StaffChecklistImageKitUploadResult);
        return;
      }
      reject(new Error(`Image upload failed with status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Image upload failed'));
    xhr.send(form);
  });
}

export type CooperativeReturnOrder = { orderId: string; currency: string; lines: Array<{ id: string; name: string; sellerName: string; parentOrderItemId?: string | null; grossMinor: number; linkedGoodsMinor?: Record<string, number>; catchUpMinor?: number; service: boolean; returned: boolean }>; corrections: Array<{ id: string; status: string }> };
export function fetchCooperativeReturnOrder(token: string, orderId: string) {
  return staffApi<CooperativeReturnOrder>('GET', `/staff/orders/${encodeURIComponent(orderId)}/cooperative-corrections`, token);
}
export function submitCooperativeReturn(token: string, orderId: string, key: string, body: { reason: string; lineIds: string[]; serviceLineIds: string[] }) {
  return staffApi<{ id: string; status: string }>('POST', `/staff/orders/${encodeURIComponent(orderId)}/cooperative-corrections`, token, body, { 'idempotency-key': key });
}
export function retryCooperativeReturn(token: string, correctionId: string) {
  return staffApi<{ id: string; status: string }>('POST', `/staff/cooperative-corrections/${encodeURIComponent(correctionId)}/retry`, token, {});
}

export type StationReturnOrderSummary = {id:string;posOrderId:string;label:string;placeName:string|null;createdAt:string;totalMinor:number;currency:string};
export function fetchStationReturnOrders(token:string,stationId:string,search='') {
  return staffApi<{orders:StationReturnOrderSummary[]}>('GET', `/staff/stations/${encodeURIComponent(stationId)}/cooperative-return-orders?search=${encodeURIComponent(search)}`,token);
}
